import bigInt from "big-integer";
import { ipToNumeric } from "lib/ip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleArrowUp,
  faCircleArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useConfig } from "app/context/config";
import { useRouteServer } from "app/context/route-servers";
import {
  useQuery,
  useQueryLocation,
  PARAM_SORT,
  PARAM_ORDER,
} from "app/context/query";
import { isUpState } from "app/components/neighbors/state";
import RelativeTimestamp from "app/components/datetime/RelativeTimestamp";
import AsnLink from "app/components/asns/AsnLink";

const querySortDefault = {
  [PARAM_SORT]: "asn",
  [PARAM_ORDER]: "asc",
};

const lookupProperty = (obj, path) => {
  let property = path.split(".").reduce((acc, part) => acc?.[part], obj);
  return typeof property === "undefined"
    ? `Property "${path}" not found`
    : property;
};

/** Funções de ordenação **/
const sortAnum = (sort) => (a, b) => (a[sort] < b[sort] ? -1 : a[sort] > b[sort] ? 1 : 0);
const sortIpAddr = (sort) => (a, b) => {
  const va = ipToNumeric(a[sort]);
  const vb = ipToNumeric(b[sort]);
  if (va instanceof bigInt) return va.compareTo(vb);
  return va < vb ? -1 : va > vb ? 1 : 0;
};
const sortOrder = (cmp, order) => (a, b) => (order === "desc" ? -cmp(a, b) : cmp(a, b));
const sortNeighbors = (neighbors, sort, order) => {
  const cmp = sort === "address" ? sortIpAddr(sort) : sortAnum(sort);
  return neighbors.sort(sortOrder(cmp, order));
};

/** Cabeçalho de seção **/
const Section = ({ state }) => {
  let sectionTitle = "";
  let badgeCls = "status-badge ";
  let sectionCls = "card-header card-header-neighbors ";
  switch (state) {
    case "up":
      sectionTitle = "BGP Sessions Established";
      sectionCls += "established ";
      badgeCls += "status-badge-success";
      break;
    case "down":
      sectionTitle = "BGP Sessions Down";
      sectionCls += "down ";
      badgeCls += "status-badge-danger";
      break;
    case "start":
    case "idle":
      sectionTitle = "BGP Sessions em transição";
      badgeCls += "status-badge-warning";
      break;
    default:
  }
  return <p className={sectionCls}><span className={badgeCls}>{sectionTitle}</span></p>;
};

/** Link de rotas **/
const RoutesLink = ({ neighbor, children }) => {
  const { routeServerId } = useParams();
  if (!isUpState(neighbor.state)) return <>{children}</>;
  const url = `/routeservers/${routeServerId}/neighbors/${neighbor.id}/routes`;
  return <Link to={url}>{children}</Link>;
};

/** Indicador de ordenação **/
const SortIndicator = ({ order, active }) => {
  if (!active) return null;
  return (
    <FontAwesomeIcon
      icon={order === "desc" ? faCircleArrowDown : faCircleArrowUp}
    />
  );
};

/** Cabeçalho das colunas **/
const ColumnHeader = ({ title, column }) => {
  const sort = column.toLowerCase();
  const [query] = useQuery(querySortDefault);
  const columnSort = useQueryLocation({
    ...query,
    [PARAM_SORT]: sort,
  });
  const toggleOrder = useQueryLocation({
    ...query,
    [PARAM_ORDER]: query[PARAM_ORDER] === "asc" ? "desc" : "asc",
  });

  const active = query[PARAM_SORT] === sort;
  let cls = `col-neighbor-attr col-neighbor-${column} `;
  let link = columnSort;
  if (active) {
    cls += "col-neighbor-active ";
    link = toggleOrder;
  }
  return (
    <th className={cls}>
      <Link to={link}>
        {title} <SortIndicator active={active} order={query[PARAM_ORDER]} />
      </Link>
    </th>
  );
};

/** Colunas **/
const ColDescription = ({ neighbor }) => (
  <td data-label="Description">
    <RoutesLink neighbor={neighbor}>
      {neighbor.description}
      {!isUpState(neighbor.state) && neighbor.last_error && (
        <span className="protocol-state-error">{neighbor.last_error}</span>
      )}
    </RoutesLink>
  </td>
);

const ColUptime = ({ neighbor }) => (
  <td data-label="Uptime" className="date-since">
    <RelativeTimestamp value={neighbor.uptime} suffix={true} />
  </td>
);

const ColLinked = ({ neighbor, column }) => {
  const property = lookupProperty(neighbor, column);
  return (
    <td data-label={column}>
      <RoutesLink neighbor={neighbor}>{property}</RoutesLink>
    </td>
  );
};

const ColPlain = ({ neighbor, column }) => {
  const property = useMemo(() => lookupProperty(neighbor, column), [neighbor, column]);
  return <td data-label={column}>{property}</td>;
};

const ColAsn = ({ neighbor }) => (
  <td data-label="ASN">
    <AsnLink asn={neighbor.asn} />
  </td>
);

const ColNotAvailable = () => <td>-</td>;

const NeighborColumn = ({ neighbor, column }) => {
  const rs = useRouteServer();
  const widgets = {
    asn: ColAsn,
    state: ColPlain,
    Uptime: ColUptime,
    Description: ColDescription,
  };

  if (rs.type === "openbgpd") {
    widgets["routes_not_exported"] = ColNotAvailable;
  }

  const Widget = widgets[column] || ColLinked;
  return <Widget neighbor={neighbor} column={column} />;
};

const NeighborRow = ({ neighbor, columns }) =>
  useMemo(() => {
    const cols = columns.map((c) => (
      <NeighborColumn key={c} neighbor={neighbor} column={c} />
    ));
    return <tr>{cols}</tr>
  }, [neighbor, columns]);

/** Tabela principal **/
const NeighborsTable = ({ neighbors, state, ref }) => {
  const config = useConfig();
  const [query] = useQuery();
  const columns = config.neighbors_columns;
  const columnsOrder = config.neighbors_columns_order;

  const sortColumn = query[PARAM_SORT];
  const sortOrder = query[PARAM_ORDER];

  const sortedNeighbors = useMemo(
    () => sortNeighbors(neighbors, sortColumn, sortOrder),
    [neighbors, sortColumn, sortOrder]
  );

  const header = useMemo(
    () =>
      columnsOrder.map((col) => (
        <ColumnHeader key={col} column={col} title={columns[col]} />
      )),
    [columns, columnsOrder]
  );

  if (!neighbors || neighbors.length === 0) return null;

  const rows = sortedNeighbors.map((neighbor) => (
    <NeighborRow key={neighbor.id} columns={columnsOrder} neighbor={neighbor} />
  ));

  return (
    <div className="card" ref={ref}>
      <Section state={state} />
      <div className="table-responsive-wrapper">
        <table className="table table-striped table-protocols">
          <thead>
            <tr>{header}</tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>

      <style>{`
        .table-protocols {
          width: 100%;
          border-collapse: collapse;
        }

        .table-protocols th,
        .table-protocols td {
          padding: 8px 12px;
          text-align: left;
          vertical-align: middle;
        }

        .table-responsive-wrapper {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .card {
          margin-bottom: 20px;
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        @media (max-width: 992px) {
          .table-protocols {
            display: block;
            width: 100%;
            white-space: nowrap;
          }

          .table-protocols th,
          .table-protocols td {
            padding: 8px 10px;
            font-size: 13px;
          }

          .table-protocols thead,
          .table-protocols tbody {
            display: table;
            width: 100%;
            table-layout: fixed;
          }

          .card {
            margin-bottom: 12px;
          }
        }

        @media (max-width: 768px) {
          .table-protocols thead {
            display: none;
          }

          .table-protocols tr {
            display: block;
            background: #fff;
            border-radius: 6px;
            margin-bottom: 10px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          }

          .table-protocols td {
            display: flex;
            justify-content: space-between;
            padding: 6px 10px;
            font-size: 13px;
            border-bottom: 1px solid #bdbcbcff;
          }

          .table-protocols td:last-child {
            border-bottom: none;
          }

          .table-protocols td::before {
            content: attr(data-label);
            font-weight: 600;
            color: #81c2ffff;
          }

          .table-responsive-wrapper {
            overflow-x: visible;
          }
        }
      `}</style>
    </div>
  );
};

export default NeighborsTable;
